import { ExercisePerformance } from '../database';
import { calculateUniversalWeight } from './weightCalculation';

export interface ProgressionResult {
  newWeight: number;
  adjustment: number;
  reason: string;
}

/**
 * Calculate the new weight for an exercise based on performance
 * 
 * Rules:
 * - Hit target reps exactly: +2.5% weight increase
 * - 1 rep over target: +3.5% weight increase  
 * - 1 rep under target: maintain same weight
 * - More than 1 rep under: maintain same weight
 * - Skipped exercise: maintain same weight
 */
export function calculateProgression(performance: ExercisePerformance): ProgressionResult {
  const currentWeight = performance.weight;
  
  // If exercise was skipped, maintain weight
  if (performance.skipped) {
    return {
      newWeight: currentWeight,
      adjustment: 0,
      reason: 'Exercise skipped - weight maintained'
    };
  }

  // Get target reps for final set
  const finalSetTarget = performance.targetSets[performance.targetSets.length - 1]?.targetReps || 0;
  const actualReps = performance.finalSetActualReps;
  const repDifference = actualReps - finalSetTarget;

  let adjustment: number;
  let reason: string;

  if (repDifference >= 1) {
    // 1+ reps over target: +3.5% increase
    adjustment = 0.035;
    reason = `Hit ${actualReps}/${finalSetTarget} reps (+${repDifference}) - increased 3.5%`;
  } else if (repDifference === 0) {
    // Hit target exactly: +2.5% increase
    adjustment = 0.025;
    reason = `Hit target ${actualReps}/${finalSetTarget} reps - increased 2.5%`;
  } else {
    // Under target: maintain weight
    adjustment = 0;
    reason = `Hit ${actualReps}/${finalSetTarget} reps (${repDifference}) - weight maintained`;
  }

  const newWeight = Math.round((currentWeight * (1 + adjustment)) * 4) / 4; // Round to nearest 0.25
  const actualAdjustment = newWeight - currentWeight;

  return {
    newWeight,
    adjustment: actualAdjustment,
    reason
  };
}

/**
 * Apply progression to a training day based on completed workout
 */
export async function applyProgressionToTrainingDay(
  trainingDayId: number,
  performances: ExercisePerformance[]
): Promise<void> {
  const { db } = await import('../database');

  try {
    // Get all training day exercises for this training day
    const trainingDayExercises = await db.trainingDayExercises
      .where('trainingDayId')
      .equals(trainingDayId)
      .toArray();

    // Update weights based on performance
    for (const exercise of trainingDayExercises) {
      const performance = performances.find(p => p.exerciseId === exercise.exerciseId);
      
      if (performance) {
        const progression = calculateProgression(performance);
        
        // Update the training day exercise with new weight
        await db.trainingDayExercises.update(exercise.id!, {
          weight: progression.newWeight
        });

        // Check if we should update the universal weight
        await updateUniversalWeightIfNeeded(performance, progression.newWeight);

      }
    }

    // Update training day timestamp
    await db.trainingDays.update(trainingDayId, {
      updatedAt: new Date()
    });

  } catch (error) {
    console.error('Failed to apply progression:', error);
    throw error;
  }
}

/**
 * Update universal weight if the progression indicates significant strength gain
 */
export async function updateUniversalWeightIfNeeded(
  performance: ExercisePerformance,
  newWeight: number
): Promise<void> {
  const { db } = await import('../database');
  
  try {
    const exercise = await db.exercises.get(performance.exerciseId);
    if (!exercise?.universalWeight || !exercise.universalWeightType) {
      return; // No universal weight to update
    }

    // Calculate what the universal weight should be based on current performance
    const finalSetTarget = performance.targetSets[performance.targetSets.length - 1]?.targetReps || 0;
    const newUniversalWeight = calculateUniversalWeight(
      newWeight,
      finalSetTarget,
      exercise.universalWeightType
    );

    // Check if the difference is significant (>5% improvement)
    const improvementThreshold = 0.05;
    const percentImprovement = (newUniversalWeight - exercise.universalWeight) / exercise.universalWeight;
    
    if (percentImprovement > improvementThreshold) {
      // Update the universal weight
      const roundedUniversalWeight = Math.round(newUniversalWeight * 4) / 4;
      
      await db.exercises.update(performance.exerciseId, {
        universalWeight: roundedUniversalWeight,
        universalWeightUpdatedAt: new Date()
      });
      
    }
  } catch (error) {
    console.error('Failed to update universal weight:', error);
    // Don't throw - this is a nice-to-have feature that shouldn't break progression
  }
}

/**
 * Get progression preview for display purposes
 */
export function getProgressionPreview(performances: ExercisePerformance[]): {
  exerciseId: number;
  currentWeight: number;
  newWeight: number;
  adjustment: number;
  reason: string;
}[] {
  return performances.map(performance => {
    const progression = calculateProgression(performance);
    return {
      exerciseId: performance.exerciseId,
      currentWeight: performance.weight,
      newWeight: progression.newWeight,
      adjustment: progression.adjustment,
      reason: progression.reason
    };
  });
}